import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import pb from '../../../lib/pocketbase';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import '../Admin.css';
import './QuotesList.css'; // Reusing wizard styles

const Billing = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ collected: 0, pending: 0 });
    const [companySettings, setCompanySettings] = useState(null);

    // Edit / Create Invoice State
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [formData, setFormData] = useState({});
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [activeTab, setActiveTab] = useState('lines'); // lines, client, vehicle, payment

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('invoices').getFullList({
                sort: '-created',
                expand: 'users'
            });
            setInvoices(records);

            // Stats
            const collected = records.filter(r => r.status === 'paid').reduce((sum, r) => sum + r.amount, 0);
            const pending = records.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.amount, 0);
            setStats({ collected, pending });

            // Fetch Company Settings for PDF
            try {
                const settings = await pb.collection('company_settings').getList(1, 1);
                if (settings && settings.items.length > 0) {
                    setCompanySettings(settings.items[0]);
                }
            } catch (e) {
                console.log("No settings found", e);
            }

        } catch (err) {
            console.error('Error fetching invoices:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    const [isCreating, setIsCreating] = useState(false);

    const handleCreateNew = () => {
        setIsCreating(true);
        setEditingInvoice({ id: 'new' });
        
        const prefix = companySettings?.num_facture_prefix || 'FAC';
        const counter = companySettings?.num_facture_counter || 0;
        const fullRef = `${prefix}-${counter.toString().padStart(3, '0')}`;

        // Default Data
        setFormData({
            invoice_number: fullRef,
            status: 'pending',
            payment_method: 'Virement',
            paid_date: '',
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +30 days
            firstName: '',
            lastName: '',
            address: '',
            email: '',
            phone: '',
            vehicle_brand: '',
            vehicle_model: '',
            vehicle_plate: '',
            vehicle_vin: '',
            vehicle_mileage: '',
            vat_rate: 0,
            deposit_amount: 0,
            public_notes: '',
            notes: '',
            amount: 0
        });
        setInvoiceLines([{
            id: Date.now(),
            description: 'Nouvelle prestation',
            quantity: 1,
            unit_price: 0,
            discount: 0,
            total: 0
        }]);
        setActiveTab('lines');
    };

    const handleEditClick = (inv) => {
        setEditingInvoice(inv);

        let lines = [];
        if (inv.invoice_lines_json) {
            try { lines = JSON.parse(inv.invoice_lines_json); } catch (e) { }
        }
        if (lines.length === 0) {
            lines = [{
                id: Date.now(),
                description: 'Prestation globale',
                quantity: 1,
                unit_price: inv.amount,
                discount: 0,
                total: inv.amount
            }];
        }

        setFormData({
            invoice_number: inv.invoice_number,
            status: inv.status,
            payment_method: inv.payment_method || '',
            paid_date: inv.paid_date ? new Date(inv.paid_date).toISOString().split('T')[0] : '',
            due_date: inv.due_date ? new Date(inv.due_date).toISOString().split('T')[0] : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],

            firstName: inv.firstName || inv.expand?.users?.firstName || '',
            lastName: inv.lastName || inv.expand?.users?.lastName || '',
            email: inv.email || inv.expand?.users?.email || '',
            phone: inv.phone || inv.expand?.users?.phone || '',
            address: inv.address || inv.expand?.users?.address || '',

            vehicle_brand: inv.vehicle_brand || '',
            vehicle_model: inv.vehicle_model || '',
            vehicle_plate: inv.vehicle_plate || '',
            vehicle_vin: inv.vehicle_vin || '',
            vehicle_mileage: inv.vehicle_mileage || '',

            vat_rate: inv.vat_rate !== undefined ? inv.vat_rate : 20,
            deposit_amount: inv.deposit_amount || 0,
            amount: inv.amount,

            public_notes: inv.public_notes || '',
            notes: inv.notes || ''
        });

        setInvoiceLines(lines);
        setActiveTab('lines');
    };

    const updateLine = (id, field, value) => {
        setInvoiceLines(prev => prev.map(line => {
            if (line.id === id) {
                const updatedLine = { ...line, [field]: value };
                const qty = field === 'quantity' ? parseFloat(value) : line.quantity;
                const price = field === 'unit_price' ? parseFloat(value) : line.unit_price;
                const discount = field === 'discount' ? parseFloat(value) : line.discount;
                updatedLine.total = (qty * price) * (1 - (discount / 100));
                return updatedLine;
            }
            return line;
        }));
    };

    const addLine = () => {
        setInvoiceLines([...invoiceLines, {
            id: Date.now(),
            description: '',
            quantity: 1,
            unit_price: 0,
            discount: 0,
            total: 0
        }]);
    };

    const removeLine = (id) => {
        setInvoiceLines(invoiceLines.filter(l => l.id !== id));
    };

    useEffect(() => {
        if (!editingInvoice) return;
        const newTotal = invoiceLines.reduce((acc, line) => acc + (line.total || 0), 0);
        setFormData(prev => ({ ...prev, amount: parseFloat(newTotal.toFixed(2)) }));
    }, [invoiceLines, editingInvoice]);

    const handleSaveInvoice = async () => {
        if (!editingInvoice) return;

        try {
            const dataToUpdate = {
                status: formData.status,
                amount: parseFloat(formData.amount),
                payment_method: formData.payment_method,
                paid_date: formData.paid_date ? new Date(formData.paid_date) : null,
                invoice_number: formData.invoice_number,

                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                vehicle_brand: formData.vehicle_brand,
                vehicle_model: formData.vehicle_model,
                vehicle_plate: formData.vehicle_plate,
                vehicle_vin: formData.vehicle_vin,
                vehicle_mileage: formData.vehicle_mileage,

                due_date: formData.due_date ? new Date(formData.due_date) : null,
                vat_rate: formData.vat_rate,
                deposit_amount: formData.deposit_amount,
                public_notes: formData.public_notes,
                notes: formData.notes,

                invoice_lines_json: JSON.stringify(invoiceLines)
            };

            if (editingInvoice.id === 'new') {
                await pb.collection('invoices').create(dataToUpdate);
                
                // Increment Counter
                if (companySettings) {
                    await pb.collection('company_settings').update(companySettings.id, {
                        'num_facture_counter+': 1
                    });
                }
            } else {
                await pb.collection('invoices').update(editingInvoice.id, dataToUpdate);
            }

            setEditingInvoice(null);
            setIsCreating(false);
            fetchInvoices();
        } catch (err) {
            console.error(err);
            alert('Erreur: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ? Cette action est irréversible.')) return;
        try {
            await pb.collection('invoices').delete(id);
            fetchInvoices();
        } catch (err) {
            console.error(err);
            alert('Erreur lors de la suppression');
        }
    };

    const handleShare = (inv) => {
        const subject = `Facture ${inv.invoice_number}`;
        const body = `Bonjour ${inv.firstName || 'Client'},\n\nVeuillez trouver ci-joint votre facture ${inv.invoice_number}.\n\nCordialement,\n${companySettings?.company_name || "L'Univers Diag"}`;
        const mailtoLink = `mailto:${inv.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailtoLink, '_blank');
    };

    const generateInvoicePDF = (data = null) => {
        let sourceData = data || formData;
        let sourceLines = [];

        if (data) {
            try {
                sourceLines = data.invoice_lines_json ? JSON.parse(data.invoice_lines_json) : [];
            } catch (e) {
                console.error(e);
                sourceLines = [];
            }
        } else {
            sourceLines = invoiceLines;
        }

        if (sourceLines.length === 0) {
            sourceLines = [{
                description: 'Prestation globale',
                quantity: 1,
                unit_price: sourceData.amount,
                discount: 0,
                total: sourceData.amount
            }];
        }

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const marginLeft = 20;

        // --- BRANDING ---
        const agencyName = companySettings?.company_name || "L'UNIVERS DIAG";
        const agencyAddress = companySettings?.address_office || companySettings?.address || "25 Rue de la Performance";
        const agencyCity = `${companySettings?.billing_zip || ''} ${companySettings?.billing_city || 'Toulouse'}`;
        const agencyPhone = companySettings?.phone_public || companySettings?.phone || "";

        // Header Logo
        if (companySettings?.logo) {
            try {
                const logoUrl = pb.getFileUrl(companySettings, companySettings.logo);
                doc.addImage(logoUrl, 'PNG', marginLeft, 15, 22, 22);
            } catch (e) {
                doc.setFontSize(22);
                doc.setTextColor(59, 130, 246);
                doc.text("💠", marginLeft, 25);
            }
        } else {
            doc.setFontSize(22);
            doc.setTextColor(59, 130, 246);
            doc.text("💠", marginLeft, 25);
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.text(agencyName, marginLeft + 30, 25);
        
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        doc.text(agencyAddress, marginLeft + 30, 31);
        doc.text(agencyCity, marginLeft + 30, 35);
        doc.text(agencyPhone, marginLeft + 30, 39);

        // Document Meta
        doc.setFontSize(24);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.text("FACTURE", pageWidth - marginLeft, 25, { align: 'right' });
        
        doc.setFontSize(10);
        doc.setTextColor(59, 130, 246);
        doc.text(sourceData.invoice_number, pageWidth - marginLeft, 32, { align: 'right' });
        
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`Le ${new Date().toLocaleDateString('fr')}`, pageWidth - marginLeft, 37, { align: 'right' });
        if (sourceData.due_date) {
            doc.text(`Échéance: ${new Date(sourceData.due_date).toLocaleDateString('fr')}`, pageWidth - marginLeft, 42, { align: 'right' });
        }

        // Section separator
        doc.setDrawColor(241, 245, 249);
        doc.line(marginLeft, 50, pageWidth - marginLeft, 50);

        // Party Details
        const startY = 62;
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text("PRESTATAIRE", marginLeft, startY);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.text(agencyName, marginLeft, startY + 7);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(agencyAddress, marginLeft, startY + 13);
        doc.text(agencyCity, marginLeft, startY + 18);

        const col2X = pageWidth / 2 + 10;
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text("FACTURÉ À", col2X, startY);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.text(`${sourceData.firstName || ''} ${sourceData.lastName || ''}`, col2X, startY + 7);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        const splitAddr = doc.splitTextToSize(sourceData.address || "Propriétaire du véhicule", 70);
        doc.text(splitAddr, col2X, startY + 13);
        
        if (sourceData.vehicle_plate) {
            doc.setFontSize(8);
            doc.setTextColor(30, 41, 59);
            const vInfo = `${sourceData.vehicle_brand || ''} ${sourceData.vehicle_model || ''} (${sourceData.vehicle_plate.toUpperCase()})`;
            doc.text(`Concerne : ${vInfo}`, col2X, startY + 28);
        }

        // Table Rows
        const tableBody = sourceLines.map(line => [
            line.description,
            line.quantity,
            `${parseFloat(line.unit_price).toFixed(2)} €`,
            line.discount ? `${line.discount}%` : '-',
            `${parseFloat(line.total).toFixed(2)} €`
        ]);

        autoTable(doc, {
            startY: startY + 35,
            head: [['Description', 'Qté', 'P.U (HT)', 'Rem.', 'Total HT']],
            body: tableBody,
            theme: 'plain',
            headStyles: { fillColor: [248, 250, 252], textColor: [100, 116, 139], fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 4 },
            columnStyles: { 4: { halign: 'right', fontStyle: 'bold' } }
        });

        // Footer Totals
        const finalY = doc.lastAutoTable.finalY + 10;
        const totalHT = parseFloat(sourceData.amount);
        const vatRate = parseFloat(sourceData.vat_rate) || 0;
        const totalTTC = totalHT + (totalHT * vatRate / 100);
        const deposit = parseFloat(sourceData.deposit_amount) || 0;
        const netToPay = totalTTC - deposit;

        const rightAlignX = pageWidth - marginLeft;
        const labelAlignX = pageWidth - marginLeft - 45;

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text("Total HT", labelAlignX, finalY, { align: 'right' });
        doc.setTextColor(0);
        doc.text(`${totalHT.toFixed(2)} €`, rightAlignX, finalY, { align: 'right' });

        if (vatRate > 0) {
            doc.text(`TVA (${vatRate}%)`, labelAlignX, finalY + 6, { align: 'right' });
            doc.text(`${(totalHT * vatRate / 100).toFixed(2)} €`, rightAlignX, finalY + 6, { align: 'right' });
        }

        if (deposit > 0) {
            doc.setTextColor(22, 163, 74);
            doc.text("Acompte perçu", labelAlignX, finalY + 12, { align: 'right' });
            doc.text(`- ${deposit.toFixed(2)} €`, rightAlignX, finalY + 12, { align: 'right' });
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(59, 130, 246);
        doc.text("NET À PAYER", labelAlignX, finalY + 22, { align: 'right' });
        doc.text(`${netToPay.toFixed(2)} €`, rightAlignX, finalY + 22, { align: 'right' });

        // Legal & Signature
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(150);
        doc.text(companySettings?.footer_facture || "TVA non applicable, art. 293 B du CGI.", marginLeft, finalY + 40);
        
        if (sourceData.status === 'paid') {
            doc.setFontSize(30);
            doc.setTextColor(16, 185, 129);
            doc.text("PAYÉE", pageWidth / 2, finalY + 30, { align: 'center', angle: 10, opacity: 0.2 });
        }

        doc.save(`Facture_${sourceData.invoice_number}.pdf`);
    };

    if (loading) return <div className="admin-content-wrapper">Chargement de la facturation...</div>;

    return (
        <div className="admin-content-wrapper">
            <section className="intro-card admin-intro">
                <div className="admin-header-content">
                    <h2>Facturation & Paiements</h2>
                    <p>Suivez vos revenus et gérez vos factures clients.</p>
                </div>
                <div className="admin-header-actions">
                    <button className="primary-button" onClick={handleCreateNew}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                        Nouvelle Facture
                    </button>
                    <button className="secondary-button" onClick={fetchInvoices}>Actualiser</button>
                </div>
            </section>

            <div className="admin-stats-grid">
                <div className="stat-card">
                    <div className="stat-icon revenue-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.collected.toLocaleString()} €</div>
                        <div className="stat-label">Encaissé total</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon pending-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.pending.toLocaleString()} €</div>
                        <div className="stat-label">En attente de paiement</div>
                    </div>
                </div>
            </div>

            <section className="card full-width-card">
                <div className="card-header-flex">
                    <h3>Historique des factures</h3>
                </div>

                <div className="table-responsive">
                    <table className="data-table admin-table">
                        <thead>
                            <tr>
                                <th>N° Facture</th>
                                <th>Client</th>
                                <th>Date</th>
                                <th>Montant HT</th>
                                <th>Statut</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr key={inv.id}>
                                    <td className="id-cell">{inv.invoice_number}</td>
                                    <td>
                                        <strong>{inv.firstName || inv.expand?.users?.name || 'Client'}</strong>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{inv.email || inv.expand?.users?.email}</div>
                                    </td>
                                    <td>{new Date(inv.created).toLocaleDateString()}</td>
                                    <td><strong>{inv.amount.toLocaleString()} €</strong></td>
                                    <td>
                                        <span className={`status-badge ${inv.status}`}>
                                            {inv.status === 'paid' ? 'Payé' : 'En attente'}
                                        </span>
                                    </td>
                                    <td className="text-right">
                                        <div className="action-buttons">

                                            {/* Download PDF */}
                                            <button
                                                className="icon-btn"
                                                title="Télécharger PDF"
                                                onClick={() => generateInvoicePDF(inv)}
                                                style={{ color: '#64748b' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                            </button>

                                            {/* Share / Email */}
                                            <button
                                                className="icon-btn"
                                                title="Envoyer par email"
                                                onClick={() => handleShare(inv)}
                                                style={{ color: '#3b82f6' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>
                                            </button>

                                            {/* Preview (Eye) */}
                                            <button
                                                className="icon-btn"
                                                title="Prévisualiser"
                                                onClick={() => setPreviewInvoice(inv)}
                                                style={{ color: '#8b5cf6' }} // Purple for view
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                            </button>

                                            {/* Edit (Pencil) */}
                                            <button
                                                className="icon-btn"
                                                title="Modifier"
                                                onClick={() => handleEditClick(inv)}
                                                style={{ color: '#3b82f6' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                            </button>

                                            {/* Delete */}
                                            <button
                                                className="icon-btn"
                                                title="Supprimer"
                                                onClick={() => handleDelete(inv.id)}
                                                style={{ color: '#ef4444' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                            </button>

                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* PREVIEW MODAL PORTAL */}
            {previewInvoice && createPortal(
                <div className="modal-backdrop" onClick={() => setPreviewInvoice(null)} style={{ zIndex: 99999 }}>
                    <div className="modal-content admin-modal-large" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="admin-modal-header">
                            <h2>Aperçu Facture {previewInvoice.invoice_number}</h2>
                            <button className="modal-close" onClick={() => setPreviewInvoice(null)}>×</button>
                        </div>
                        <div className="admin-modal-body" style={{ padding: '2rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                                <div>
                                    <h3 style={{ color: '#3b82f6', marginBottom: '0.5rem' }}>L'Univers Diag</h3>
                                    <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>{companySettings?.address || 'Toulouse'}</p>
                                    <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>{companySettings?.email || 'contact@luniversdiag.fr'}</p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Date: {new Date(previewInvoice.created).toLocaleDateString()}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b' }}>N°: {previewInvoice.invoice_number}</div>
                                    <div className={`status-badge ${previewInvoice.status}`} style={{ marginTop: '0.5rem', display: 'inline-block' }}>
                                        {previewInvoice.status === 'paid' ? 'Payée' : 'En attente'}
                                    </div>
                                </div>
                            </div>

                            <div style={{ padding: '1.5rem', background: 'rgba(15, 23, 42, 0.3)', borderRadius: '12px', marginBottom: '2rem' }}>
                                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#64748b', marginBottom: '1rem', fontWeight: 'bold' }}>Facturé à</div>
                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#f8fafc' }}>
                                    {previewInvoice.firstName} {previewInvoice.lastName}
                                </div>
                                <div style={{ color: '#94a3b8', marginTop: '0.2rem' }}>{previewInvoice.address}</div>
                                <div style={{ color: '#94a3b8' }}>{previewInvoice.email}</div>
                                <div style={{ color: '#94a3b8' }}>{previewInvoice.phone}</div>
                            </div>

                            <div style={{ marginBottom: '2rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', paddingBottom: '0.5rem', borderBottom: '1px solid #334155', color: '#94a3b8', fontSize: '0.9rem' }}>
                                    <div>Description</div>
                                    <div style={{ textAlign: 'right' }}>Montant</div>
                                </div>
                                {(() => {
                                    let lines = [];
                                    try { lines = JSON.parse(previewInvoice.invoice_lines_json); } catch (e) { }
                                    if (lines.length === 0) lines = [{ description: 'Prestation globale', total: previewInvoice.amount }];
                                    return lines.map((line, idx) => (
                                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px', padding: '0.8rem 0', borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
                                            <div style={{ color: '#e2e8f0' }}>
                                                {line.description}
                                                {line.quantity > 1 && <span style={{ fontSize: '0.8rem', color: '#94a3b8', marginLeft: '0.5rem' }}>x{line.quantity}</span>}
                                            </div>
                                            <div style={{ textAlign: 'right', fontWeight: '600', color: '#f8fafc' }}>
                                                {parseFloat(line.total).toFixed(2)} €
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '3rem' }}>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: '#94a3b8', marginBottom: '0.5rem' }}>Total HT</div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>{previewInvoice.amount.toLocaleString()} €</div>
                                    {previewInvoice.status === 'pending' && <div style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '0.5rem' }}>À régler avant le {new Date(previewInvoice.due_date).toLocaleDateString()}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="secondary-button" onClick={() => generateInvoicePDF(previewInvoice)}> Télécharger PDF</button>
                            <button className="primary-button" onClick={() => { setPreviewInvoice(null); handleEditClick(previewInvoice); }}>Modifier</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* POWER-WIZARD MODAL - INVOICE VERSION (OPTION 1) */}
            {editingInvoice && createPortal(
                <div className="modal-backdrop" onClick={() => { setEditingInvoice(null); setIsCreating(false); }} style={{ zIndex: 99999 }}>
                    <div className="modal-content quote-split-wizard" onClick={e => e.stopPropagation()}>
                        
                        {/* LEFT: UNIFIED INPUT PANEL */}
                        <div className="wizard-input-panel" style={{ flex: '0 0 520px' }}>
                            <div className="wizard-header">
                                <div className="admin-header-main">
                                    <h2>{editingInvoice.id === 'new' ? 'Assistant Facturation' : 'Édition Facture'}</h2>
                                    <span className="subtitle-admin">{formData.invoice_number}</span>
                                </div>
                                <button className="modal-close-mini" onClick={() => { setEditingInvoice(null); setIsCreating(false); }}>×</button>
                            </div>

                            <div className="wizard-body unified-scroll-body" style={{ padding: '25px' }}>
                                {/* SECTION 1: PRESTATIONS (The big table) */}
                                <section className="wizard-form-section">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <h3 className="section-title-neo" style={{ border: 'none', marginBottom: 0 }}>1. Détail des Prestations</h3>
                                        <button className="add-btn-mini" onClick={addLine}>+ Ajouter Ligne</button>
                                    </div>
                                    
                                    <div className="prestations-header" style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) 45px 70px 60px 80px 25px', gap: '8px', fontSize: '0.65rem', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <div>Désignation</div>
                                        <div style={{ textAlign: 'center' }}>Qté</div>
                                        <div>P.U</div>
                                        <div>Rem.%</div>
                                        <div style={{ textAlign: 'right' }}>Total</div>
                                        <div></div>
                                    </div>

                                    <div className="invoice-lines-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                                        {invoiceLines.map((line) => (
                                            <div key={line.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) 45px 70px 60px 80px 25px', gap: '8px', alignItems: 'center' }}>
                                                <input className="admin-input" style={{ padding: '6px 10px', fontSize: '0.8rem' }} value={line.description} onChange={(e) => updateLine(line.id, 'description', e.target.value)} placeholder="Main d'oeuvre, Pièce..." />
                                                <input className="admin-input" style={{ padding: '6px', textAlign: 'center', fontSize: '0.8rem' }} type="number" value={line.quantity} onChange={(e) => updateLine(line.id, 'quantity', e.target.value)} />
                                                <input className="admin-input" style={{ padding: '6px', fontSize: '0.8rem' }} type="number" value={line.unit_price} onChange={(e) => updateLine(line.id, 'unit_price', e.target.value)} />
                                                <input className="admin-input" style={{ padding: '6px', fontSize: '0.8rem' }} type="number" value={line.discount} onChange={(e) => updateLine(line.id, 'discount', e.target.value)} />
                                                <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#f8fafc', textAlign: 'right' }}>{parseFloat(line.total).toFixed(2)}€</div>
                                                <button onClick={() => removeLine(line.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ marginTop: '15px', padding: '15px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '12px', textAlign: 'right' }}>
                                        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>TOTAL HT : </span>
                                        <strong style={{ fontSize: '1.2rem', color: '#3b82f6' }}>{formData.amount?.toLocaleString()} €</strong>
                                    </div>
                                </section>

                                {/* SECTION 2: CLIENT */}
                                <section className="wizard-form-section">
                                    <h3 className="section-title-neo">2. Informations Client</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div className="form-group">
                                            <label>Prénom</label>
                                            <input className="admin-input" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} />
                                        </div>
                                        <div className="form-group">
                                            <label>Nom</label>
                                            <input className="admin-input" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} />
                                        </div>
                                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                            <label>Adresse de Facturation</label>
                                            <input className="admin-input" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Adresse complète" />
                                        </div>
                                        <div className="form-group">
                                            <label>Email</label>
                                            <input className="admin-input" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                                        </div>
                                        <div className="form-group">
                                            <label>Téléphone</label>
                                            <input className="admin-input" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                                        </div>
                                    </div>
                                </section>

                                {/* SECTION 3: VEHICULE */}
                                <section className="wizard-form-section">
                                    <h3 className="section-title-neo">3. Informations Véhicule</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div className="form-group">
                                            <label>Immatriculation</label>
                                            <input className="admin-input" value={formData.vehicle_plate} onChange={e => setFormData({...formData, vehicle_plate: e.target.value.toUpperCase()})} style={{ textTransform: 'uppercase' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Kilométrage (Kms)</label>
                                            <input className="admin-input" type="number" value={formData.vehicle_mileage} onChange={e => setFormData({...formData, vehicle_mileage: e.target.value})} />
                                        </div>
                                        <div className="form-group">
                                            <label>Marque</label>
                                            <input className="admin-input" value={formData.vehicle_brand} onChange={e => setFormData({...formData, vehicle_brand: e.target.value})} />
                                        </div>
                                        <div className="form-group">
                                            <label>Modèle</label>
                                            <input className="admin-input" value={formData.vehicle_model} onChange={e => setFormData({...formData, vehicle_model: e.target.value})} />
                                        </div>
                                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                            <label>VIN (Numéro de série)</label>
                                            <input className="admin-input" value={formData.vehicle_vin} onChange={e => setFormData({...formData, vehicle_vin: e.target.value})} maxLength={17} />
                                        </div>
                                    </div>
                                </section>

                                {/* SECTION 4: REGLAGE & PAIEMENT */}
                                <section className="wizard-form-section">
                                    <h3 className="section-title-neo">4. Règlement & Taxes</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div className="form-group">
                                            <label>Statut</label>
                                            <select className="admin-input" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value, paid_date: e.target.value === 'paid' ? new Date().toISOString().split('T')[0] : ''})}>
                                                <option value="pending">En attente</option>
                                                <option value="paid">Payée</option>
                                                <option value="cancelled">Annulée</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Mode</label>
                                            <select className="admin-input" value={formData.payment_method} onChange={e => setFormData({...formData, payment_method: e.target.value})}>
                                                <option value="Virement">Virement</option>
                                                <option value="Espèces">Espèces</option>
                                                <option value="CB">Carte Bancaire</option>
                                                <option value="Chèque">Chèque</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>TVA (%)</label>
                                            <input type="number" className="admin-input" value={formData.vat_rate} onChange={e => setFormData({...formData, vat_rate: e.target.value})} />
                                        </div>
                                        <div className="form-group">
                                            <label>Acompte versé (€)</label>
                                            <input type="number" className="admin-input" value={formData.deposit_amount} onChange={e => setFormData({...formData, deposit_amount: e.target.value})} />
                                        </div>
                                        <div className="form-group">
                                            <label>Date échéance</label>
                                            <input type="date" className="admin-input" value={formData.due_date} onChange={e => setFormData({...formData, due_date: e.target.value})} />
                                        </div>
                                        <div className="form-group">
                                            <label>Notes Facture</label>
                                            <input className="admin-input" value={formData.public_notes} onChange={e => setFormData({...formData, public_notes: e.target.value})} placeholder="Notes publiques" />
                                        </div>
                                    </div>
                                </section>
                            </div>

                            <div className="wizard-footer-panel">
                                <button className="secondary-button" onClick={() => { setEditingInvoice(null); setIsCreating(false); }}>Annuler</button>
                                <button className="complete-btn-neo-large" onClick={handleSaveInvoice}>Enregistrer la Facture</button>
                            </div>
                        </div>

                        {/* RIGHT: LIVE DOCUMENT PREVIEW */}
                        <div className="wizard-preview-panel">
                            <button className="download-pdf-btn-icon" onClick={() => generateInvoicePDF()} title="Télécharger le PDF">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"></path></svg>
                            </button>
                            <div className="quote-paper-container">
                                <div className="quote-preview-paper" style={{ padding: '40px' }}>
                                    <header className="quote-doc-header" style={{ marginBottom: '40px' }}>
                                        <div className="agency-brand">
                                            <div className="brand-logo-preview">
                                                {companySettings ? (
                                                    <img 
                                                        src={pb.getFileUrl(companySettings, companySettings.logo_light || companySettings.logo)} 
                                                        alt="Logo" 
                                                        style={{ height: '40px', objectFit: 'contain' }} 
                                                    />
                                                ) : '💠'}
                                            </div>
                                            <div className="brand-text">
                                                <span className="name">{companySettings?.company_name || "L'UNIVERS DIAG"}</span>
                                                <span className="sub">{companySettings?.slogan || "Automobile & Performance"}</span>
                                            </div>
                                        </div>
                                        <div className="doc-meta">
                                            <h2 style={{ fontSize: '1.8rem' }}>FACTURE</h2>
                                            <span className="ref">{formData.invoice_number}</span>
                                            <span className="date">Le {new Date().toLocaleDateString('fr')}</span>
                                        </div>
                                    </header>

                                    <div className="quote-parties-grid" style={{ marginBottom: '40px', gap: '20px' }}>
                                        <div className="party-box from">
                                            <span className="party-label">Prestataire</span>
                                            <strong>{companySettings?.company_name || "L'Univers Diag"}</strong>
                                            <p>{companySettings?.address_office || companySettings?.address || '25 Rue de la Performance'}</p>
                                            <p>{companySettings?.billing_zip || ''} {companySettings?.billing_city || 'Toulouse'}</p>
                                            <p>{companySettings?.phone_public || companySettings?.phone || ''}</p>
                                        </div>
                                        <div className="party-box to">
                                            <span className="party-label">Facturé à</span>
                                            <strong>{formData.firstName || formData.lastName ? `${formData.firstName} ${formData.lastName}` : 'Client non renseigné'}</strong>
                                            <p>{formData.email || 'N/A'}</p>
                                            <p>{formData.address || ''}</p>
                                            {formData.vehicle_plate && <p style={{ fontWeight: 'bold' }}>{formData.vehicle_brand} {formData.vehicle_model} - {formData.vehicle_plate}</p>}
                                        </div>
                                    </div>

                                    <table className="quote-table-preview" style={{ marginBottom: '30px' }}>
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th className="text-right">Total HT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceLines.map((line, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ padding: '12px 15px' }}>
                                                        {line.description}
                                                        {line.quantity > 1 && <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '5px' }}>x{line.quantity}</span>}
                                                    </td>
                                                    <td className="text-right" style={{ padding: '12px 15px' }}>{parseFloat(line.total).toFixed(2)} €</td>
                                                </tr>
                                            ))}
                                            {invoiceLines.length === 0 && (
                                                <tr>
                                                    <td colSpan="2" className="text-center empty-doc">Aucune prestation...</td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td className="total-label" style={{ padding: '10px 15px' }}>TOTAL HT</td>
                                                <td className="total-value" style={{ padding: '10px 15px', fontSize: '1.4rem' }}>{formData.amount?.toFixed(2)} €</td>
                                            </tr>
                                            {parseFloat(formData.vat_rate) > 0 && (
                                                <tr>
                                                    <td className="total-label" style={{ padding: '5px 15px', fontSize: '0.9rem', color: '#64748b' }}>TVA ({formData.vat_rate}%)</td>
                                                    <td className="total-value" style={{ padding: '5px 15px', fontSize: '0.9rem', color: '#64748b' }}>{(formData.amount * formData.vat_rate / 100).toFixed(2)} €</td>
                                                </tr>
                                            )}
                                            {parseFloat(formData.deposit_amount) > 0 && (
                                                <tr>
                                                    <td className="total-label" style={{ padding: '5px 15px', fontSize: '0.9rem', color: '#16a34a' }}>Acompte déjà versé</td>
                                                    <td className="total-value" style={{ padding: '5px 15px', fontSize: '0.9rem', color: '#16a34a' }}>- {parseFloat(formData.deposit_amount).toFixed(2)} €</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td className="total-label" style={{ padding: '15px 15px', fontSize: '1.2rem', borderTop: '2px solid #0f172a' }}>NET À PAYER</td>
                                                <td className="total-value" style={{ padding: '15px 15px', fontSize: '1.8rem', color: '#3b82f6', borderTop: '2px solid #0f172a' }}>
                                                    {(formData.amount + (formData.amount * (formData.vat_rate || 0) / 100) - (formData.deposit_amount || 0)).toFixed(2)} €
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>

                                    <footer className="quote-doc-footer" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                                        <div className="legal-notes">
                                            <p>{companySettings?.footer_facture || "TVA non applicable, art. 293 B du CGI."}</p>
                                            <p style={{ marginTop: '10px' }}><strong>Règlement : {formData.payment_method}</strong></p>
                                            {formData.due_date && <p>Échéance le : {new Date(formData.due_date).toLocaleDateString('fr')}</p>}
                                            {companySettings?.bank_iban && (
                                                <div className="bank-details-box-mini" style={{ marginTop: '15px', fontSize: '0.75rem', opacity: 0.8, background: '#f8fafc', padding: '10px', borderRadius: '8px' }}>
                                                    <strong>Coordonnées bancaires :</strong><br/>
                                                    {companySettings.bank_name} - IBAN : {companySettings.bank_iban}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="signature-area" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <div className={`status-pill ${formData.status}`} style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '12px', border: '3px solid', transform: 'rotate(-5deg)', fontWeight: '900', fontSize: '1.2rem', textTransform: 'uppercase', marginBottom: '20px' }}>
                                                {formData.status === 'paid' ? 'FACTURÉE & PAYÉE' : (formData.status === 'cancelled' ? 'ANNULÉE' : 'À RÉGLER')}
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                <strong style={{ fontSize: '0.9rem' }}>{pb.authStore.model?.firstName} {pb.authStore.model?.lastName}</strong><br/>
                                                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Gérant de l'Agence</span>
                                            </div>
                                        </div>
                                    </footer>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Billing;
